import React, { useRef, useEffect, useState } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import APIHelper from "@giscon/utils.api-helper-esri";
import { overviewMapLayerUrl } from "configs/config";

const OverviewMap = ({
  map,
  view,
  token,
  layers = [
    {
      type: "featureLayer",
      name: "UmringNiedersachsen",
      url: overviewMapLayerUrl,
    },
  ],
}) => {
  const container = useRef();
  const [omap, setMap] = useState();
  const [extent, setExtent] = useState();
  const [layer, setLayer] = useState();
  const appliedLayersRef = useRef(null);

  // const [bglayers, setbglayers] = useState([]);
  const [overviewExtent, setOverviewExtent] = useState();

  /**
   * https://developers.arcgis.com/javascript/latest/sample-code/sandbox/?sample=view-disable-navigation
   *
   * Disables all zoom gestures on the given view instance.
   *
   * @param {esri/views/MapView} view - The MapView instance on which to
   *                                  disable zooming gestures.
   */
  const disableZooming = (view) => {
    view.popup.dockEnabled = true;

    // Removes the zoom action on the popup
    view.popup.actions = [];

    // stops propagation of default behavior when an event fires
    function stopEvtPropagation(event) {
      event.stopPropagation();
    }

    // exlude the zoom widget from the default UI
    view.ui.components = ["attribution"];

    // disable mouse wheel scroll zooming on the view
    view.on("mouse-wheel", stopEvtPropagation);

    // disable zooming via double-click on the view
    view.on("double-click", stopEvtPropagation);

    // disable zooming out via double-click + Control on the view
    view.on("double-click", ["Control"], stopEvtPropagation);

    // disables pinch-zoom and panning on the view
    view.on("drag", stopEvtPropagation);

    // disable the view's zoom box to prevent the Shift + drag
    // and Shift + Control + drag zoom gestures.
    view.on("drag", ["Shift"], stopEvtPropagation);
    view.on("drag", ["Shift", "Control"], stopEvtPropagation);

    // prevents zooming with the + and - keys
    view.on("key-down", (event) => {
      const prohibitedKeys = [
        "+",
        "-",
        "Shift",
        "_",
        "=",
        "ArrowUp",
        "ArrowDown",
        "ArrowRight",
        "ArrowLeft",
      ];
      const keyPressed = event.key;
      if (prohibitedKeys.indexOf(keyPressed) !== -1) {
        event.stopPropagation();
      }
    });
    return view;
  };

  useEffect(() => {
    if (!omap || !layer) return;

    // Build a stable key from layer content to avoid rebuilding on every render
    const layersKey =
      (layers || [])
        .map(({ url, name }) => `${url || ""}|${name || ""}`)
        .join(";;") + `||${token || ""}`;

    // Only rebuild if the actual layer config changed
    if (appliedLayersRef.current === layersKey) return;
    appliedLayersRef.current = layersKey;

    omap.removeAll();
    if (layers && layers.length) {
      omap.addMany(
        layers.map(({ url, name }) => {
          return APIHelper.createImageLayer(
            { url: url, name: name },
            { token } /* { id, name } */
          );
        })
      );
    }
    omap.add(layer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, omap, token, layer]);

  useEffect(() => {
    if (container.current) {
      var overviewMap = new Map({
        basemap: {
          portalItem: {
            id: "d7e7aece4aa0467c857b29ecdd7f3749",
          },
        },
      });

      var overviewMapView = new MapView({
        container: container.current,
        map: overviewMap,
        constraints: {
          rotationEnabled: false,
        },
      });

      overviewMapView.when(() => {
        overviewMapView.popup.actions = [];
        setMap(overviewMap);
        disableZooming(overviewMapView);
        const gl = APIHelper.createGraphicsLayer();
        APIHelper.addToMap(overviewMap, gl);
        setLayer(gl);
        setExtent(view.extent);
        overviewMapView.center = [32510407, 5829897];
        overviewMapView.zoom = 6;
        let oldExt = view.extent;
        setOverviewExtent(overviewMapView.extent);
        view.watch("extent", (ext) => {
          // update shown extent
          if (!ext.equals(oldExt)) {
            setExtent(view.extent);
            oldExt = view.extent;
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container.current]);

  useEffect(() => {
    if (extent && layer && overviewExtent) {
      APIHelper.clearLayer(layer);
      const geo = APIHelper.differenceOfGeometries(
        APIHelper.createPolygonFromExtent(overviewExtent),
        APIHelper.createPolygonFromExtent(extent)
      );
      if (geo) {
        APIHelper.addGraphicToLayer(
          APIHelper.createGraphicWithStandardSymbol(geo, null, "mask"),
          layer
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extent, overviewExtent, layer]);

  return (
    <div
      id="overviewMap"
      style={{
        width: "100%",
        height: "100%",
        border: "3px solid grey",
        boxSizing: "border-box",
      }}
      ref={container}
    />
  );
};

export default OverviewMap;
